<template>
  <div class="safe-area">
    <!-- Router View с анимациями -->
    <div class="pages-container">
      <router-view v-slot="{ Component, route }">
        <transition :name="transitionName" mode="out-in">
          <component :is="Component" :key="route.path" />
        </transition>
      </router-view>
    </div>

    <!-- Global Bottom Navigation - с анимацией появления -->
    <transition name="nav-slide">
      <div class="bottom-nav" v-if="showBottomNav">
        <div
          class="nav-item"
          :class="{ active: currentRoute === '/' }"
          @click="goTo('')"
        >
          <div class="nav-icon">🏠</div>
          <div class="nav-label">Главная</div>
        </div>
        <div
          class="nav-item"
          :class="{ active: currentRoute === '/buyVPN' }"
          @click="goTo('buyVPN')"
        >
          <div class="nav-icon">🛒</div>
          <div class="nav-label">Купить</div>
        </div>
        <div
          class="nav-item"
          :class="{ active: currentRoute === '/referal' }"
          @click="goTo('referal')"
        >
          <div class="nav-icon">🎁</div>
          <div class="nav-label">Реферал</div>

        </div>
        <div
          class="nav-item"
          :class="{ active: currentRoute === '/profile' }"
          @click="goTo('profile')"
        >
          <div class="nav-icon">👤</div>
          <div class="nav-label">Профиль</div>

        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { initTelegram, requestFullscreen, getSafeAreaInsets, isMobileDevice } from './utils/telegram';
import { haptic } from './utils/telegram';

const router = useRouter();
const route = useRoute();
const transitionName = ref('slide-left');

const safeArea = ref(getSafeAreaInsets({
  top: { value: isMobileDevice() ? 12 : 0, unit: 'vh' },
}));

// Определяем порядок страниц в навигации
const pageOrder: Record<string, number> = {
  '/': 0,
  '/buyVPN': 1,
  '/referal': 2,
  '/profile': 3,
  '/deposit': 10,
  '/tasks': 10,
  '/ChooseOSView': 10,
  '/payment-history': 10,
};

// Страницы где показывать bottom-nav (основные страницы)
const pagesWithNav = ['/', '/buyVPN', '/referal', '/profile'];

let previousPageIndex = 0;

const currentRoute = computed(() => route.path);

// Показывать bottom-nav только на основных страницах
const showBottomNav = computed(() => {
  return pagesWithNav.includes(route.path);
});

watch(
  () => route.path,
  (newPath, oldPath) => {
    const newIndex = pageOrder[newPath] ?? -1;
    const oldIndex = pageOrder[oldPath] ?? previousPageIndex;

    // Определяем направление анимации
    if (newIndex >= 10 && oldIndex < 10) {
      // Открытие модальной страницы
      transitionName.value = 'slide-up';
    } else if (oldIndex >= 10 && newIndex < 10) {
      // Закрытие модальной страницы
      transitionName.value = 'slide-down';
    } else if (newIndex > oldIndex) {
      // Переход вперед
      transitionName.value = 'slide-left';
    } else if (newIndex < oldIndex) {
      // Переход назад
      transitionName.value = 'slide-right';
    } else {
      // Неопределенный переход
      transitionName.value = 'fade';
    }

    previousPageIndex = newIndex !== -1 ? newIndex : oldIndex;
  }
);

const goTo = (route: string) => {
  haptic.impact();
  router.push(`/${route}`);
};

// ✅ ИСПРАВЛЕНО: Асинхронная инициализация с обработкой ошибок
onMounted(async () => {
  try {
    console.log('🚀 Starting Telegram initialization...');

    // Ждём загрузки и инициализации Telegram SDK
    await initTelegram();

    // После успешной инициализации запрашиваем fullscreen
    requestFullscreen();

    console.log('✅ App.vue: Telegram initialized successfully');
  } catch (error) {
    console.error('❌ App.vue: Failed to initialize Telegram:', error);

    // Можно показать fallback UI или уведомление пользователю
    // alert('Не удалось инициализировать Telegram WebApp. Пожалуйста, откройте приложение через Telegram.');
  }
});
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');

body {
  margin: 0;
  font-family: "Roboto", sans-serif;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  min-height: 100vh;
  overflow: hidden;
  position: relative;
}

body::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 20% 80%, rgba(255, 107, 53, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 107, 157, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(192, 108, 132, 0.1) 0%, transparent 50%);
  pointer-events: none;
  z-index: 0;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  margin: 0;
  overflow: hidden;
  line-height: 1.5;
  position: relative;
  z-index: 1;
}

.safe-area {
  width: 100%;
  height: 100%;
  padding: v-bind('safeArea.top.value + safeArea.top.unit')
          v-bind('safeArea.right?.value + (safeArea.right?.unit ?? "px")')
          v-bind('safeArea.bottom?.value + (safeArea.bottom?.unit ?? "px")')
          v-bind('safeArea.left?.value + (safeArea.left?.unit ?? "px")');
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.pages-container {
  flex: 1;
  overflow: hidden;
  position: relative;
}

/* ============================================
   ANIMATED BOTTOM NAVIGATION
   ============================================ */

/* Анимация появления/исчезновения nav */
.nav-slide-enter-active {
  animation: navSlideUp 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}

.nav-slide-leave-active {
  animation: navSlideDown 0.3s cubic-bezier(0.4, 0, 0.6, 1);
}

@keyframes navSlideUp {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(100px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
}

@keyframes navSlideDown {
  0% {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(100px) scale(0.9);
  }
}

.bottom-nav {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 40px);
  max-width: 400px;
  background: rgba(26, 31, 41, 0.8);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  display: flex;
  justify-content: space-around;
  padding: 12px 8px;
  z-index: 1000;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4),
              0 0 0 0.5px rgba(255, 255, 255, 0.05) inset;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 6px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 14px;
  position: relative;
  overflow: visible;
}

/* Анимация при клике */
.nav-item:active {
  transform: scale(0.88);
}

/* Hover эффект (для десктопа) */
@media (hover: hover) {
  .nav-item:hover:not(.active) {
    color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.05);
  }
}

/* Активное состояние */
.nav-item.active {
  color: #FF6B35;
  background: rgba(255, 107, 53, 0.15);
}

/* Анимация иконки */
.nav-icon {
  font-size: 22px;
  transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
  position: relative;
  z-index: 2;
}

/* Иконка активной вкладки - bounce эффект */
.nav-item.active .nav-icon {
  animation: iconBounce 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes iconBounce {
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.2) translateY(-2px);
  }
  60% {
    transform: scale(0.95);
  }
  80% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

/* Анимация label */
.nav-label {
  font-size: 11px;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 2;
}

.nav-item.active .nav-label {
  font-weight: 700;
}

/* Анимированный индикатор активной вкладки */
.active-indicator {
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  background: linear-gradient(135deg, #FF6B35, #F7931E);
  border-radius: 50%;
  animation: indicatorPulse 1.5s ease-in-out infinite;
  box-shadow: 0 0 8px rgba(255, 107, 53, 0.6);
}

@keyframes indicatorPulse {
  0%, 100% {
    transform: translateX(-50%) scale(1);
    opacity: 1;
  }
  50% {
    transform: translateX(-50%) scale(1.3);
    opacity: 0.7;
  }
}

/* Дополнительная анимация для активного элемента */
.nav-item.active::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 14px;
  background: linear-gradient(135deg,
    rgba(255, 107, 53, 0.1),
    rgba(247, 147, 30, 0.1)
  );
  opacity: 0;
  animation: glowPulse 2s ease-in-out infinite;
  z-index: 0;
}

@keyframes glowPulse {
  0%, 100% {
    opacity: 0;
    transform: scale(0.9);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

/* ============================================
   БЫСТРЫЕ АНИМАЦИИ СТРАНИЦ
   ============================================ */

.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
  filter: blur(10px);
}

.slide-left-enter-to {
  opacity: 1;
  transform: translateX(0) scale(1);
  filter: blur(0);
}

.slide-left-leave-from {
  opacity: 1;
  transform: translateX(0) scale(1);
  filter: blur(0);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-30%) scale(0.95);
  filter: blur(5px);
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-30%) scale(0.95);
  filter: blur(5px);
}

.slide-right-enter-to {
  opacity: 1;
  transform: translateX(0) scale(1);
  filter: blur(0);
}

.slide-right-leave-from {
  opacity: 1;
  transform: translateX(0) scale(1);
  filter: blur(0);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
  filter: blur(10px);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateY(100%) scale(0.95);
}

.slide-up-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.slide-up-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateY(-10%) scale(0.98);
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-10%) scale(0.98);
}

.slide-down-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.slide-down-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(100%) scale(0.95);
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from {
  opacity: 0;
  transform: scale(0.98);
  filter: blur(5px);
}

.fade-enter-to {
  opacity: 1;
  transform: scale(1);
  filter: blur(0);
}

.fade-leave-from {
  opacity: 1;
  transform: scale(1);
  filter: blur(0);
}

.fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
  filter: blur(5px);
}

@media (prefers-reduced-motion: no-preference) {
  .slide-left-enter-active,
  .slide-left-leave-active,
  .slide-right-enter-active,
  .slide-right-leave-active,
  .slide-up-enter-active,
  .slide-up-leave-active,
  .slide-down-enter-active,
  .slide-down-leave-active {
    transition-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
  }
}

.slide-left-leave-active,
.slide-right-leave-active,
.slide-up-leave-active,
.slide-down-leave-active,
.fade-leave-active {
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
}

.slide-left-leave-active *,
.slide-right-leave-active *,
.slide-up-leave-active *,
.slide-down-leave-active * {
  user-select: none;
  -webkit-user-select: none;
}

::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 107, 53, 0.5);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 107, 53, 0.7);
}
</style>